import { useState, useCallback, useMemo } from "react";
import {
  UniversalTable,
  TableColumnsConfig,
} from "shared/components/UniversalTable";
import { TableSortConfig } from "shared/components/UniversalTable/types/SortConfig";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Button } from "components/ui/button";
import { Plus } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import * as Sentry from "@sentry/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCompanyEquipmentData } from "./hooks/useCompanyEquipmentData";
import {
  COMPANY_EQUIPMENT_COLUMNS,
  COMPANY_EQUIPMENT_FILTER_SECTIONS,
  COMPANY_EQUIPMENT_GROUPBY_OPTIONS,
} from "./config/companyEquipmentTableConfig";
import { createCompanyEquipmentCells } from "./config/companyEquipmentCellFactory";
import { CompanyEquipmentModal } from "./components/CompanyEquipmentModal";
import { CompanyEquipment } from "./types/companyEquipment";

export const CompanyEquipmentPage = () => {
  const { session } = useSession();
  const { equipment, isLoading, refresh } = useCompanyEquipmentData();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CompanyEquipment | null>(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<CompanyEquipment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter & grouping state
  const [searchText, setSearchText] = useState("");
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [companyFilters, setCompanyFilters] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string>("none");

  // Build filter sections with dynamic company options from data
  const filterSectionsWithData = useMemo(() => {
    return COMPANY_EQUIPMENT_FILTER_SECTIONS.map((section) => {
      if (section.extractOptionsFromData) {
        return {
          ...section,
          options: section.extractOptionsFromData(equipment),
        };
      }
      return section;
    });
  }, [equipment]);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (key: string, values: string[]) => {
      switch (key) {
        case "companyEquipmentType":
          setTypeFilters(values);
          break;
        case "company":
          setCompanyFilters(values);
          break;
      }
    },
    [],
  );

  // Client-side filtering
  const filteredEquipment = useMemo(() => {
    let result = equipment;

    // Search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      result = result.filter((row) => {
        return (
          String(row.id).includes(search) ||
          (row.serialNumber && row.serialNumber.toLowerCase().includes(search)) ||
          (row.companyEquipmentType && row.companyEquipmentType.toLowerCase().includes(search)) ||
          (row.companyName && row.companyName.toLowerCase().includes(search)) ||
          (row.inUseBy && row.inUseBy.toLowerCase().includes(search))
        );
      });
    }

    // Type filter
    if (typeFilters.length > 0) {
      result = result.filter(
        (row) =>
          row.companyEquipmentType && typeFilters.includes(row.companyEquipmentType),
      );
    }

    // Company filter
    if (companyFilters.length > 0) {
      result = result.filter(
        (row) => row.companyId && companyFilters.includes(row.companyId),
      );
    }

    return result;
  }, [equipment, searchText, typeFilters, companyFilters]);

  // Edit handler
  const handleEditClick = useCallback((item: CompanyEquipment) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  // Close modal and clear editing state
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  // Open create modal
  const handleCreateClick = useCallback(() => {
    setEditingItem(null);
    setModalOpen(true);
  }, []);

  // Delete handlers
  const handleDeleteClick = useCallback((item: CompanyEquipment) => {
    setDeletingItem(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingItem) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/company-equipment/${deletingItem.id}`,
        { method: "DELETE" },
        session,
      );

      if (!response.ok) {
        throw new Error(`Fout bij verwijderen: ${response.status}`);
      }

      toast.success("Bedrijfsuitrusting succesvol verwijderd!");
      refresh();
    } catch (error: any) {
      console.error("Error deleting company equipment:", error);
      Sentry.captureException(error, {
        tags: { component: "CompanyEquipmentPage", action: "delete" },
        extra: { equipmentId: deletingItem.id },
      });
      toast.error(
        `Fout bij verwijderen: ${error.message || "Onbekende fout"}`,
      );
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingItem(null);
    }
  }, [deletingItem, session, refresh]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeletingItem(null);
  }, []);

  // Render row using cell factory
  const renderRow = useCallback(
    (row: CompanyEquipment) => {
      return createCompanyEquipmentCells(row, {
        onEditClick: handleEditClick,
        onDeleteClick: handleDeleteClick,
      });
    },
    [handleEditClick, handleDeleteClick],
  );

  // Sort config for all columns
  const sortConfig: TableSortConfig = useMemo(
    () => ({
      fields: [
        { key: "id", extractor: (row: CompanyEquipment) => row.id },
        { key: "serialNumber", extractor: (row: CompanyEquipment) => row.serialNumber || "" },
        {
          key: "companyEquipmentType",
          extractor: (row: CompanyEquipment) => row.companyEquipmentType || "",
        },
        { key: "company", extractor: (row: CompanyEquipment) => row.companyName || "" },
        {
          key: "inUseBy",
          extractor: (row: CompanyEquipment) => row.inUseBy || "",
        },
      ],
      defaultSortKey: "id",
      defaultDirection: "asc",
    }),
    [],
  );

  return (
    <HelmetProvider>
      <Helmet>
        <title>Bedrijfsuitrusting — Yally</title>
      </Helmet>

      <div className="flex h-full flex-col">
        <div className="flex items-center justify-end px-4 pt-4">
          <Button onClick={handleCreateClick} size="sm">
            <Plus size={16} className="mr-1" />
            Toevoegen
          </Button>
        </div>

        <UniversalTable
          columnsConfig={
            {
              columns: COMPANY_EQUIPMENT_COLUMNS,
              sortConfig: {
                defaultSortKey: "id",
                defaultDirection: "asc",
              } as TableSortConfig,
            } as TableColumnsConfig
          }
          tableId="company-equipment-v1"
          rows={filteredEquipment}
          title="Bedrijfsuitrusting"
          description="Overzicht van alle bedrijfsuitrusting"
          groupBy={groupBy}
          isLoading={isLoading}
          sortConfig={sortConfig}
          searchConfig={{
            searchPlaceholder: "Zoeken op serienummer, type, bedrijf of gebruiker...",
            filterSections: filterSectionsWithData,
            groupByOptions: COMPANY_EQUIPMENT_GROUPBY_OPTIONS,
            onGroupByChange: setGroupBy,
            searchValue: searchText,
            onSearchChange: setSearchText,
            activeFilters: {
              companyEquipmentType: typeFilters,
              company: companyFilters,
            },
            onFilterChange: handleFilterChange,
          }}
          renderRow={renderRow}
          showQuickAdd={true}
          quickAddLabel="Snel uitrusting toevoegen"
          onQuickAdd={handleCreateClick}
        />

        <CompanyEquipmentModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSuccess={refresh}
          editingItem={editingItem}
        />

        <AlertDialog open={deleteDialogOpen} onOpenChange={handleDeleteCancel}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Bedrijfsuitrusting verwijderen</AlertDialogTitle>
              <AlertDialogDescription>
                Weet je zeker dat je{" "}
                {deletingItem?.serialNumber && (
                  <strong>{deletingItem.serialNumber}</strong>
                )}{" "}
                wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>
                Annuleren
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? "Bezig met verwijderen..." : "Verwijderen"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </HelmetProvider>
  );
};
